import { useState, useEffect, useMemo } from 'react';
import {
  AlertCircle, AlertTriangle, Loader2, Mail, Send, SkipForward,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { runPool } from '../../utils/runPool';
import { fmtDate } from '../../lib/reportFormulas';
import { planBulkSend, type BulkSendInput, type BulkSendRow } from '../../lib/bulkSend';
import { reportSendStatus, canSend as reportCanBeSent, SEND_STATUS_LABELS, NO_SENDS } from '../../lib/reportStatus';
import {
  bvReportService, type BvBillsMailbox, type BvEmailTemplate,
} from '../../services/bvReportService';
import { bulkReportService } from '../../services/bulkReportService';
import type { BvBill } from '../../types/billTypes';

type BulkSendReportsDialogProps = {
  open: boolean;
  /** `queuedAny` — a batch was created; refresh the batches panel and send status. */
  onClose: (queuedAny: boolean) => void;
  /** Bills to send. The page only lets not-sent and send-failed reports be selected. */
  bills: BvBill[];
};

type Phase = 'loading' | 'confirm' | 'queueing' | 'queued';

/**
 * Queue many reports with one template and one mailbox.
 *
 * The bills are checked here exactly as before (recipients, skipped rows,
 * warnings). Sending is done by server-side workers in the background (spec
 * 2026-10-16): each report is still BillReportScreen captured in a browser —
 * a headless one on the server — so the adviser can close this dialog, the
 * page or the tab. Progress is on the Report batches panel.
 */
export const BulkSendReportsDialog = ({ open, onClose, bills }: BulkSendReportsDialogProps) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [templates, setTemplates] = useState<BvEmailTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [mailboxes, setMailboxes] = useState<BvBillsMailbox[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);

  const [rows, setRows] = useState<BulkSendRow[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);

  // Re-read from the database on open (see load). The page's copies can predate
  // a re-run, and the PDF, the email and the "still not sent" check must all
  // agree on one set of figures.
  const [freshById, setFreshById] = useState<Map<string, BvBill>>(new Map());
  const pageById = useMemo(() => new Map(bills.map((b) => [b.id, b])), [bills]);
  const billFor = (id: string) => freshById.get(id) ?? pageById.get(id);
  // The selection is fixed for the dialog's lifetime; a key of the ids is enough
  // to reload when a different set is opened.
  const billKey = bills.map((b) => b.id).join(',');

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setPhase('loading');
    setLoadError(null);
    setQueueError(null);
    setBatchId(null);
    setRows([]);
    setFreshById(new Map());

    const load = async () => {
      const [tplRes, mailRes] = await Promise.all([
        bvReportService.listEmailTemplates(),
        bvReportService.listMailboxes(),
      ]);
      if (!mounted) return;
      if (!tplRes.success) { setLoadError(tplRes.error ?? 'Could not load the email templates'); return; }
      if (!mailRes.success) { setLoadError(mailRes.error ?? 'Could not load the bills mailbox'); return; }
      const tpls = tplRes.data ?? [];
      setTemplates(tpls);
      setTemplateId(tpls.find((t) => t.is_default)?.id ?? tpls[0]?.id ?? null);
      setMailboxes(mailRes.data ?? []);
      setAccountId(mailRes.data?.[0]?.id ?? null);

      const freshRes = await bulkReportService.getBillsForSend(bills.map((b) => b.id));
      if (!mounted) return;
      if (!freshRes.success || !freshRes.data) { setLoadError(freshRes.error ?? 'Could not re-read the selected bills'); return; }
      const fresh = new Map<string, BvBill>(freshRes.data.bills.map((b) => [b.id, b]));
      setFreshById(fresh);

      const inputs = new Map<string, BulkSendInput>();
      const stillSendable = bills.filter((b) => {
        const current = fresh.get(b.id);
        if (!current) {
          inputs.set(b.id, { billId: b.id, context: null, error: 'This bill no longer exists or was archived' });
          return false;
        }
        const history = freshRes.data!.history[b.id] ?? NO_SENDS;
        const status = reportSendStatus(current, history);
        if (!reportCanBeSent(current, history)) {
          inputs.set(b.id, {
            billId: b.id, context: null,
            error: status === 'no_report'
              ? 'This bill no longer has a report — it needs an expected invoice and a retailer invoice'
              : `Already sent since this page loaded (now: ${SEND_STATUS_LABELS[status]})`,
          });
          return false;
        }
        return true;
      });

      await runPool(stillSendable, 4, async (bill) => {
        const res = await bvReportService.context(bill.id);
        inputs.set(bill.id, {
          billId: bill.id,
          context: res.success ? res.data ?? null : null,
          error: res.success ? null : res.error ?? 'Could not read this bill’s figures',
        });
      }, (bill, err) => {
        inputs.set(bill.id, { billId: bill.id, context: null, error: err instanceof Error ? err.message : 'Could not read this bill’s figures' });
      });
      if (!mounted) return;
      // Keep the page's order, not the order the requests happened to finish in.
      setRows(planBulkSend(bills.map((b) => inputs.get(b.id) ?? { billId: b.id, context: null, error: 'Not loaded' })));
      setPhase('confirm');
    };
    void load();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on a new selection, not on a new array identity
  }, [open, billKey]);

  const ready = rows.filter((r) => r.state === 'ready');
  const skipped = rows.filter((r) => r.state === 'skipped');
  const canSend = phase === 'confirm' && ready.length > 0 && mailboxes.length > 0 && !!accountId;

  const handleQueue = async () => {
    if (!accountId) return;
    setPhase('queueing');
    setQueueError(null);
    const res = await bvReportService.createBatch({
      billIds: ready.map((r) => r.billId),
      recipientsByBill: Object.fromEntries(ready.map((r) => [r.billId, r.recipients])),
      templateId,
      accountId,
    });
    if (!res.success || !res.data) {
      setQueueError(res.error ?? 'Could not start the background send');
      setPhase('confirm');
      return;
    }
    setBatchId(res.data.batchId);
    setPhase('queued');
  };

  const close = () => {
    if (phase === 'queueing') return;
    onClose(batchId !== null);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent data-testid="bulk-send-dialog" className="max-w-4xl max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" /> Send {bills.length} report{bills.length === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          {loadError && (
            <div className="flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> {loadError}
            </div>
          )}

          {phase === 'loading' && !loadError && (
            <div className="flex items-center gap-2 py-10 justify-center text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading each bill’s figures and recipients…
            </div>
          )}

          {phase !== 'loading' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Email template</span>
                  <select
                    value={templateId ?? ''}
                    onChange={(e) => setTemplateId(e.target.value || null)}
                    disabled={phase !== 'confirm'}
                    className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-white disabled:bg-gray-50"
                  >
                    {templates.length === 0 && <option value="">Standard bill validation email</option>}
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Send from</span>
                  <select
                    value={accountId ?? ''}
                    onChange={(e) => setAccountId(e.target.value || null)}
                    disabled={phase !== 'confirm' || mailboxes.length === 0}
                    className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-white disabled:bg-gray-50"
                  >
                    {mailboxes.map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}
                    {mailboxes.length === 0 && <option value="">No bills mailbox connected</option>}
                  </select>
                </label>
              </div>

              {mailboxes.length === 0 && (
                <p className="flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  No active bills mailbox is connected. Connect one under Bill Validation → Inbox first.
                </p>
              )}

              <p className="text-xs text-gray-500">
                Each email is filled with its own bill’s figures and goes to the client contacts on file.
                {skipped.length > 0 && ` ${skipped.length} bill${skipped.length === 1 ? '' : 's'} will be skipped.`}
                {' '}To change recipients or wording for one bill, use Send to client on that report instead.
              </p>

              {queueError && (
                <p className="flex gap-2 items-start text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {queueError}
                </p>
              )}
              {phase === 'queued' && (
                <p data-testid="bulk-send-queued" className="flex gap-2 items-start text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5">
                  <Send className="h-4 w-4 shrink-0 mt-0.5" />
                  Sending {ready.length} report{ready.length === 1 ? '' : 's'} in the background. You can close this — progress, pause and any failures are on the Report batches panel.
                </p>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs text-gray-500">
                      <th className="px-3 py-2 font-medium">Client · NMI</th>
                      <th className="px-3 py-2 font-medium">Period</th>
                      <th className="px-3 py-2 font-medium">Recipients</th>
                      <th className="px-3 py-2 font-medium text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => {
                      const bill = billFor(row.billId);
                      return (
                        <tr key={row.billId} data-testid="bulk-send-row" data-state={row.state} className="align-top">
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900">{bill?.site?.client?.name ?? '—'}</p>
                            <p className="text-xs font-mono text-gray-500">{bill?.site?.nmi ?? '—'}</p>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                            {bill ? `${fmtDate(bill.bill_from)} – ${fmtDate(bill.bill_to)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            <p className="break-words">{row.recipients.join(', ') || '—'}</p>
                            {row.warnings.length > 0 && (
                              <p className="text-amber-700 mt-0.5 break-words">
                                <AlertTriangle className="inline h-3 w-3 mr-0.5" />
                                {row.warnings.join(' · ')}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                            <RowStatus row={row} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
          <Button data-testid="bulk-send-close" variant="outline" onClick={close} disabled={phase === 'queueing'}>
            {phase === 'queued' ? 'Close' : 'Cancel'}
          </Button>
          {(phase === 'confirm' || phase === 'queueing') && (
            <Button data-testid="bulk-send-submit" onClick={() => void handleQueue()} disabled={!canSend} className="gap-1.5">
              {phase === 'queueing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send {ready.length} in background
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RowStatus = ({ row }: { row: BulkSendRow }) => {
  if (row.state === 'skipped') {
    return (
      <span className={cn('inline-flex items-start gap-1 text-gray-500 whitespace-normal text-left max-w-[220px]')}>
        <SkipForward className="h-3 w-3 shrink-0 mt-0.5" /> {row.reason}
      </span>
    );
  }
  return <span className="text-gray-500">Ready</span>;
};
